/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react/forbid-prop-types */

import React, {
  memo,
  useRef,
  useEffect,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import moment from 'moment';
import 'moment/locale/es';
import DateMomentUtils from '@date-io/moment';

import useEnabler from '../../../hooks/useEnabler';

import useStyles from './useStyles';
import CustomToolbar from '../../CustomToolbar';

const LOCALE = 'es';
const ERROR_MESSAGE = 'La fecha no está en el formato correcto:';
const ERROR_MESSAGE_EMPTY = 'Rellena este campo';
const ERROR_MESSAGE_OUT_OF_RANGE = 'La fecha no está en el rango permitido';
moment.locale(LOCALE);
const FORMAT = 'DD/MM/YYYY';

const convertingDate = (valueDate, oldFormat, newFormat = FORMAT) => {
  const parsedDate = moment(valueDate, oldFormat);
  const formattedDate = parsedDate.format(newFormat);
  return formattedDate;
};

const InputBaseDatePicker = ({
  id,
  name,
  label,
  format,
  minDateRange,
  maxDateRange,
  value,
  required,
  isEmpty,
  disabled,
  className,
  fullWidth,
  onChange,
  onKeyDown,
  rejections,
}) => {
  const dateInputFormat = FORMAT;
  const classes = useStyles();
  const inputRef = useRef();
  const [date, setDate] = useState(null);
  const [isRangeValid, setIsRangeValid] = useState(true);

  const {
    isEnabled: isRejected,
    enable: enableRejected,
  } = useEnabler();

  const {
    isEnabled: isError,
    enable: enableError,
    disable: disableError,
  } = useEnabler();

  const isDateInRange = (dateValue) => {
    if (!minDateRange || !maxDateRange) return true;
    const dateMoment = moment(dateValue);
    const dateYears = dateMoment.year();
    if (!dateYears) return false;
    const todayYears = moment().year();
    const yearsDiff = Math.abs(dateYears - todayYears);
    return yearsDiff >= minDateRange && yearsDiff <= maxDateRange;
  };

  const getErrorMessage = () => {
    let message = value ? `${ERROR_MESSAGE} ${dateInputFormat}` : ERROR_MESSAGE_EMPTY;
    if (!value && isError) return ERROR_MESSAGE_EMPTY;
    message = isError ? message : '';
    if (isRejected) message = `${rejections.reason}-${rejections.comments}`;
    if (!isRangeValid) message = ERROR_MESSAGE_OUT_OF_RANGE;
    return message;
  };

  useEffect(() => {
    if (rejections) {
      enableRejected();
    }
  }, [enableRejected, rejections]);

  useEffect(() => {
    if (isEmpty) {
      enableError();
    }
  }, [enableError, isEmpty]);

  const formatDate = (_value) => {
    if (!_value) return null;
    const dateParsed = Date.parse(_value);
    if (Number.isNaN(dateParsed)) {
      return moment(_value, dateInputFormat);
    }
    return moment(_value);
  };

  const handleDateChange = (event) => {
    setDate(formatDate(event));
    const isDateValid = isDateInRange(event);
    if (!isDateValid) {
      setIsRangeValid(false);
      enableError();
    } else {
      setIsRangeValid(true);
      disableError();
    }
    const formattedDate = dateInputFormat && event ? moment(event).format(format) : event;
    // setDate(formattedDate);
    onChange({ target: { value: event, name } }, formattedDate);
  };

  const validateDate = (dateToValidate) => {
    if (!dateToValidate) return false;

    const momentDate = moment.isMoment(dateToValidate) ? dateToValidate : moment(dateToValidate);
    const isValidDate = momentDate.isValid();
    const isRangeOk = isDateInRange(momentDate);

    if (!isValidDate) {
      enableError();
      return false;
    }

    if (!isRangeOk) {
      setIsRangeValid(false);
      enableError();
      return false;
    }

    setIsRangeValid(true);
    disableError();
    return true;
  };

  const onBlur = () => {
    validateDate(date);
  };

  useEffect(() => {
    // Ignorar valores inválidos como "Fecha Invalida" o "Invalid Date"
    if (!value || value === 'Fecha Invalida' || value === 'Invalid Date') {
      return;
    }

    // Verificar que el value sea una fecha válida antes de procesarlo
    const parsedValue = moment(value, format, true);
    if (!parsedValue.isValid()) {
      return;
    }

    const newValue = convertingDate(value, format, 'MM/DD/YYYY');
    if (date !== newValue) {
      const formattedDate = formatDate(newValue);
      setDate(formattedDate);
      // Validar la fecha cuando se carga el componente con un valor inicial
      validateDate(formattedDate);
    }
  }, [value]);

  useEffect(() => {
    if (!inputRef.current) return;

    const [input] = inputRef.current.getElementsByTagName('input');
    const message = getErrorMessage();
    if (isError) input.setCustomValidity(message);
    else input.setCustomValidity('');
  }, [isError, isRangeValid]);

  // GALJ950602HMC
  return (
    <MuiPickersUtilsProvider utils={DateMomentUtils} locale={LOCALE}>
      <KeyboardDatePicker
        id={id}
        name={name}
        label={label}
        placeholder="DD/MM/AAAA"
        value={date}
        ToolbarComponent={(props) => <CustomToolbar {...props} />}
        required={required}
        disabled={disabled}
        format={dateInputFormat}
        className={clsx(classes.textField, className)}
        fullWidth={fullWidth}
        variant="inline"
        inputVariant="filled"
        InputLabelProps={{
          classes: {
            root: classes.InputLabelPropsRoot,
            focused: classes.InputLabelPropsFocused,
          },
        }}
        InputProps={{
          ref: inputRef,
          classes: {
            root: classes.InputPropsRoot,
            focused: classes.InputPropsFocused,
            input: classes.InputPropsInput,
            disabled: classes.InputPropsDisabled,
            error: classes.InputPropsError,
          },
          disableUnderline: true,
        }}
        onChange={handleDateChange}
        onKeyDown={onKeyDown}
        error={isError || isRejected}
        onBlur={onBlur}
        helperText={getErrorMessage()}
        autoOk
      />
    </MuiPickersUtilsProvider>
  );
};

InputBaseDatePicker.propTypes = {
  id: PropTypes.any,
  name: PropTypes.any,
  label: PropTypes.string,
  value: PropTypes.any,
  format: PropTypes.string,
  required: PropTypes.bool,
  isEmpty: PropTypes.any,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  fullWidth: PropTypes.bool,
  onKeyDown: PropTypes.func,
  rejections: PropTypes.any,
  onChange: PropTypes.func,
  minDateRange: PropTypes.number,
  maxDateRange: PropTypes.number,
};

InputBaseDatePicker.defaultProps = {
  id: undefined,
  name: undefined,
  label: '',
  value: undefined,
  required: false,
  isEmpty: undefined,
  format: 'DD/MM/YYYY',
  disabled: false,
  className: '',
  fullWidth: true,
  onChange: () => { },
  onKeyDown: () => { },
  rejections: null,
  minDateRange: null,
  maxDateRange: null,
};

export default memo(InputBaseDatePicker);
